import { memo, useState, useCallback, useEffect, useRef, type JSX } from 'react'

import { SmepExpManifest } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState } from '@tanstack/react-table'
import { noop, pick, omit } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, NewDialog, RowActions, SelectionToolbarActions } from 'domains/Workspaces/ExpManifests'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { expManifestsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/ExpManifests'
import { expManifestRawRoute } from 'pages/NWPlatform/Workspaces/Workspace/ExpManifests/ExpManifest'
import { expManifestQueryKeys, workspaceQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: expManifestsOverviewRoute.fullPath })
  const { workspaceId } = expManifestsOverviewRoute.useParams()
  const { query } = expManifestsOverviewRoute.useSearch()

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const tableInstanceRef = useRef<ReactTableType<SmepExpManifest>>(undefined)

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const expManifestsQuery = useInfiniteQuery({
    ...expManifestQueryKeys.list({
      repositoryId: workspaceQuery.data?.contentRepositoryId as string,
      view: 'Full',
      query,
    }),
    enabled: workspaceQuery.isSuccess && !!workspaceQuery.data.contentRepositoryId,
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })
  const expManifests = expManifestsQuery.data?.flat

  const columns = useColumns()

  useEffect(() => {
    if (expManifestsQuery.isSuccess) {
      tableInstanceRef.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = expManifests?.find((manifest) => manifest.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [expManifests, expManifestsQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.ExpManifest.searchPrograms')}
          onSearch={({ search }) =>
            navigate({ search: (current) => (search.length ? { ...current, query: search } : omit(current, 'query')) })
          }
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: expManifestQueryKeys.list({
                  repositoryId: workspaceQuery.data?.contentRepositoryId as string,
                  view: 'Full',
                  query,
                }).queryKey,
              }),
            [query, queryClient, workspaceQuery.data?.contentRepositoryId],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(tableInstance) => void (tableInstanceRef.current = tableInstance)}
        size={48}
        data={expManifests ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || expManifestsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={expManifestsOverviewRoute.fullPath}
            to={expManifestRawRoute.to}
            params={{ expManifestId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', 'contentRepositoryId']}
        enableInfinity
        infinityProps={pick(expManifestsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: noop,
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.expManifest', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} repositoryId={workspaceQuery.data!.contentRepositoryId} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.expManifest', { count: 2 })}
            discardSelection={() => tableInstanceRef.current?.resetRowSelection()}
            Actions={
              tableInstanceRef.current && (
                <SelectionToolbarActions selectedRows={selectedRows} tableInstance={tableInstanceRef.current} />
              )
            }
          />
        )}
      />

      <Dialog open={newDialogOpen} onClose={useCallback(() => setNewDialogOpen(false), [])}>
        <NewDialog />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
